/**
 * Pipeline List - Browse and manage pipelines
 */

import React, { useEffect, useMemo, useState } from 'react';
import pipelineApi from '../api/client';
import type { PipelineDefinition } from '../types/pipeline';
import { ExecutionWizard } from './ExecutionWizard';
import { LoadingScreen } from './LoadingScreen';
import { copyToClipboard } from '../utils/clipboard';
import { CopyIcon, PlusIcon, TrashIcon, DuplicateIcon } from './icons';
import {
  CollectionToolbar,
  SortableHeader,
  SortOption,
  compareValues,
  stopRowClick,
  useCollectionPrefs,
} from './CollectionToolbar';
import { formatRelative, formatTimestamp, timestampValue } from '../utils/formatDate';
import './PipelineList.css';

interface PipelineListProps {
  onOpenPipeline: (pipeline: PipelineDefinition) => void | Promise<void>;
  // Opens a copy of the workflow as an unsaved new one in the builder.
  onDuplicatePipeline: (pipeline: PipelineDefinition) => void | Promise<void>;
  onCreateNew: () => void;
  onOpenExecution: (executionId: string) => void;
}

type WorkflowSortKey = 'name' | 'updated' | 'created' | 'nodes';

const WORKFLOW_SORTS: SortOption<WorkflowSortKey>[] = [
  { key: 'updated', label: 'Last saved', defaultDirection: 'desc' },
  { key: 'name', label: 'Name', defaultDirection: 'asc' },
  { key: 'created', label: 'Created', defaultDirection: 'desc' },
  { key: 'nodes', label: 'Nodes', defaultDirection: 'desc' },
];

const UPDATED_WITHIN = [
  { id: 'any', label: 'Any time', ms: Infinity },
  { id: '1d', label: 'Last 24h', ms: 86_400_000 },
  { id: '7d', label: 'Last 7 days', ms: 7 * 86_400_000 },
  { id: '30d', label: 'Last 30 days', ms: 30 * 86_400_000 },
] as const;

const workflowSortValue = (p: PipelineDefinition, key: WorkflowSortKey) => {
  switch (key) {
    case 'name':
      return p.metadata.name;
    case 'updated':
      return timestampValue(p.metadata.updated_at);
    case 'created':
      return timestampValue(p.metadata.created_at);
    case 'nodes':
      return p.node_count ?? p.nodes.length;
  }
};

// Load persisted search from localStorage
const loadPersistedSearch = () => {
  try {
    return localStorage.getItem('raiqon_workflow_search') || '';
  } catch (e) {
    console.error('Failed to load workflow search:', e);
    return '';
  }
};

export const PipelineList: React.FC<PipelineListProps> = ({
  onOpenPipeline,
  onDuplicatePipeline,
  onCreateNew,
  onOpenExecution,
}) => {
  const [pipelines, setPipelines] = useState<PipelineDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(loadPersistedSearch());
  const [deleteMode, setDeleteMode] = useState(false);
  const [pipelinesToDelete, setPipelinesToDelete] = useState<Set<string>>(new Set());
  const [executingPipeline, setExecutingPipeline] = useState<PipelineDefinition | null>(null);
  const [prefs, setPrefs] = useCollectionPrefs<WorkflowSortKey>(
    'raiqon_workflow_view',
    { view: 'grid', sortKey: 'updated', sortDirection: 'desc' },
    WORKFLOW_SORTS.map(o => o.key)
  );
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [updatedWithin, setUpdatedWithin] = useState<(typeof UPDATED_WITHIN)[number]['id']>('any');

  useEffect(() => {
    loadPipelines();
  }, []);

  // Persist search when it changes
  useEffect(() => {
    try {
      localStorage.setItem('raiqon_workflow_search', searchQuery);
    } catch (e) {
      console.error('Failed to save workflow search:', e);
    }
  }, [searchQuery]);

  const loadPipelines = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pipelineApi.listPipelines();
      setPipelines(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pipelines');
      console.error('Failed to load pipelines:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setPipelinesToDelete(new Set());
  };

  const togglePipelineForDeletion = (pipelineId: string) => {
    const newSet = new Set(pipelinesToDelete);
    if (newSet.has(pipelineId)) {
      newSet.delete(pipelineId);
    } else {
      newSet.add(pipelineId);
    }
    setPipelinesToDelete(newSet);
  };

  const handleDeleteSelected = async () => {
    if (pipelinesToDelete.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${pipelinesToDelete.size} pipeline(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        Array.from(pipelinesToDelete).map(id => pipelineApi.deletePipeline(id))
      );
      await loadPipelines();
      setPipelinesToDelete(new Set());
      setDeleteMode(false);
    } catch (err) {
      setError('Failed to delete pipelines: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteClick = (pipeline: PipelineDefinition, event: React.MouseEvent) => {
    event.stopPropagation();
    setExecutingPipeline(pipeline);
  };

  const allTags = useMemo(
    () => Array.from(new Set(pipelines.flatMap(p => p.metadata.tags))).sort((a, b) => a.localeCompare(b)),
    [pipelines]
  );

  const toggleTag = (tag: string) =>
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });

  // Search, tag (must have every selected tag) and last-saved filters, then the chosen sort.
  const query = searchQuery.toLowerCase();
  const maxAge = UPDATED_WITHIN.find(w => w.id === updatedWithin)?.ms ?? Infinity;
  const filteredPipelines = pipelines
    .filter(pipeline =>
      pipeline.metadata.name.toLowerCase().includes(query) ||
      pipeline.metadata.description?.toLowerCase().includes(query) ||
      pipeline.metadata.tags.some(tag => tag.toLowerCase().includes(query))
    )
    .filter(pipeline => Array.from(selectedTags).every(tag => pipeline.metadata.tags.includes(tag)))
    .filter(pipeline => {
      if (maxAge === Infinity) return true;
      const updated = timestampValue(pipeline.metadata.updated_at);
      return updated !== undefined && Date.now() - updated <= maxAge;
    })
    .sort((a, b) =>
      compareValues(workflowSortValue(a, prefs.sortKey), workflowSortValue(b, prefs.sortKey), prefs.sortDirection)
    );
  const filtersActive = selectedTags.size > 0 || updatedWithin !== 'any';

  if (loading) {
    return (
      <div className="pipeline-list-container">
        <LoadingScreen label="Loading pipelines..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pipeline-list-container">
        <div className="pipeline-list-error">
          <strong>Error:</strong> {error}
          <button className="button-secondary" onClick={loadPipelines}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pipeline-list-container">
      <div className="pipeline-list-header">
        <div>
          <h1>Workflows</h1>
          <p className="text-secondary">Build and manage your AI processing workflows</p>
        </div>
        <div className="pipeline-header-actions">
          {deleteMode ? (
            <>
              <button onClick={toggleDeleteMode} className="button-secondary">
                Cancel
              </button>
              <button
                onClick={handleDeleteSelected}
                className="button-danger"
                disabled={pipelinesToDelete.size === 0}
              >
                Delete ({pipelinesToDelete.size})
              </button>
            </>
          ) : (
            <>
              <button onClick={toggleDeleteMode} className="button-icon-large" title="Delete workflows">
                <TrashIcon size={16} />
              </button>
              <button className="button-primary" onClick={onCreateNew}>
                <PlusIcon /> Create Workflow
              </button>
            </>
          )}
        </div>
      </div>

      {pipelines.length > 0 && (
        <div className="pipeline-search">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="search-icon">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
          </svg>
          <input
            type="text"
            placeholder="Search workflows by name, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
      )}

      {pipelines.length > 0 && (
        <div className="pipeline-filters">
          <label className="pipeline-filters__updated">
            Saved
            <select value={updatedWithin} onChange={(e) => setUpdatedWithin(e.target.value as typeof updatedWithin)}>
              {UPDATED_WITHIN.map(w => (
                <option key={w.id} value={w.id}>{w.label}</option>
              ))}
            </select>
          </label>
          {allTags.length > 0 && (
            <div className="pipeline-filters__tags">
              {allTags.map(tag => (
                <button
                  key={tag}
                  className={`pipeline-filters__tag ${selectedTags.has(tag) ? 'is-active' : ''}`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
          {filtersActive && (
            <button
              className="pipeline-filters__clear"
              onClick={() => {
                setSelectedTags(new Set());
                setUpdatedWithin('any');
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {pipelines.length > 0 && (
        <CollectionToolbar
          prefs={prefs}
          onChange={setPrefs}
          sortOptions={WORKFLOW_SORTS}
          resultCount={filteredPipelines.length}
          totalCount={pipelines.length}
          noun="workflows"
        />
      )}

      {pipelines.length === 0 ? (
        <div className="pipeline-list-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="currentColor" opacity="0.3">
            <rect x="8" y="8" width="20" height="20" rx="2" />
            <rect x="36" y="8" width="20" height="20" rx="2" />
            <rect x="8" y="36" width="20" height="20" rx="2" />
            <rect x="36" y="36" width="20" height="20" rx="2" />
          </svg>
          <h3>No workflows yet</h3>
          <p>Create your first workflow to get started</p>
          <button className="button-primary" onClick={onCreateNew}>
            Create Workflow
          </button>
        </div>
      ) : filteredPipelines.length === 0 ? (
        <div className="pipeline-list-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="currentColor" opacity="0.3">
            <path d="M46.742 42.344a26.5 26.5 0 1 0-4.397 4.398h-.001c.03.04.062.078.098.115l15.85 15.85a4 4 0 0 0 5.415-5.414l-15.85-15.85a4.007 4.007 0 0 0-.115-.1zM48 26.5a21.5 21.5 0 1 1-43 0 21.5 21.5 0 0 1 43 0z"/>
          </svg>
          <h3>No workflows found</h3>
          <p>{searchQuery ? `No workflows match "${searchQuery}" with the current filters` : 'No workflows match the current filters'}</p>
          <button
            className="button-secondary"
            onClick={() => {
              setSearchQuery('');
              setSelectedTags(new Set());
              setUpdatedWithin('any');
            }}
          >
            Clear search and filters
          </button>
        </div>
      ) : prefs.view === 'list' ? (
        <div className="collection-table-wrap">
          <table className="collection-table">
            <thead>
              <tr>
                {deleteMode && <th className="collection-table__check" />}
                <SortableHeader label="Name" sortKey="name" prefs={prefs} sortOptions={WORKFLOW_SORTS} onChange={setPrefs} />
                <th>Tags</th>
                <SortableHeader label="Nodes" sortKey="nodes" prefs={prefs} sortOptions={WORKFLOW_SORTS} onChange={setPrefs} />
                <SortableHeader label="Last saved" sortKey="updated" prefs={prefs} sortOptions={WORKFLOW_SORTS} onChange={setPrefs} />
                <SortableHeader label="Created" sortKey="created" prefs={prefs} sortOptions={WORKFLOW_SORTS} onChange={setPrefs} />
                {!deleteMode && <th className="collection-table__actions" />}
              </tr>
            </thead>
            <tbody>
              {filteredPipelines.map(pipeline => (
                <tr
                  key={pipeline.id}
                  className={pipelinesToDelete.has(pipeline.id) ? 'is-marked' : ''}
                  onClick={() => (deleteMode ? togglePipelineForDeletion(pipeline.id) : onOpenPipeline(pipeline))}
                >
                  {deleteMode && (
                    <td className="collection-table__check">
                      <input
                        type="checkbox"
                        checked={pipelinesToDelete.has(pipeline.id)}
                        onChange={() => togglePipelineForDeletion(pipeline.id)}
                        onClick={stopRowClick}
                      />
                    </td>
                  )}
                  <td>
                    <span className="collection-table__name">{pipeline.metadata.name}</span>
                    {pipeline.metadata.description && (
                      <span className="collection-table__sub">{pipeline.metadata.description}</span>
                    )}
                  </td>
                  <td>
                    <div className="collection-table__tags">
                      {pipeline.metadata.tags.map(tag => (
                        <span key={tag} className="collection-table__tag">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="collection-table__muted">
                    {pipeline.node_count ?? pipeline.nodes.length} nodes · {pipeline.edge_count ?? pipeline.edges.length} links
                  </td>
                  <td className="collection-table__muted" title={formatTimestamp(pipeline.metadata.updated_at)}>
                    {formatRelative(pipeline.metadata.updated_at)}
                  </td>
                  <td className="collection-table__muted" title={formatTimestamp(pipeline.metadata.created_at)}>
                    {formatRelative(pipeline.metadata.created_at)}
                  </td>
                  {!deleteMode && (
                    <td className="collection-table__actions" onClick={stopRowClick}>
                      <div className="collection-table__actions-inner">
                        <button className="collection-table__action" onClick={() => onDuplicatePipeline(pipeline)} title="Duplicate workflow">
                          <DuplicateIcon />
                        </button>
                        <button
                          className="collection-table__action"
                          onClick={() => {
                            copyToClipboard(pipeline.id);
                          }}
                          title="Copy workflow ID"
                        >
                          <CopyIcon />
                        </button>
                        <button
                          className="collection-table__action"
                          onClick={(e) => handleExecuteClick(pipeline, e)}
                          title="Execute workflow"
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M4 2l10 6-10 6V2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="pipeline-grid">
          {filteredPipelines.map(pipeline => (
            <div
              key={pipeline.id}
              className={`pipeline-card ${deleteMode ? 'delete-mode' : ''} ${pipelinesToDelete.has(pipeline.id) ? 'marked-for-deletion' : ''}`}
              onClick={() => deleteMode ? togglePipelineForDeletion(pipeline.id) : onOpenPipeline(pipeline)}
            >
              {deleteMode && (
                <div className="pipeline-delete-checkbox">
                  <input
                    type="checkbox"
                    checked={pipelinesToDelete.has(pipeline.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      togglePipelineForDeletion(pipeline.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              <div className="pipeline-card-header">
                <h3>{pipeline.metadata.name}</h3>
                {!deleteMode && (
                  <div className="pipeline-card-actions">
                    <button
                      className="pipeline-card-duplicate"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicatePipeline(pipeline);
                      }}
                      title="Duplicate workflow"
                    >
                      <DuplicateIcon size={16} />
                    </button>
                    <button
                      className="pipeline-card-execute"
                      onClick={(e) => handleExecuteClick(pipeline, e)}
                      title="Execute pipeline"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M4 2l10 6-10 6V2z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {pipeline.metadata.description && (
                <p className="pipeline-card-description">{pipeline.metadata.description}</p>
              )}

              <div className="pipeline-card-stats">
                <div className="stat">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="2" y="2" width="5" height="5" rx="1" />
                    <rect x="9" y="2" width="5" height="5" rx="1" />
                    <rect x="2" y="9" width="5" height="5" rx="1" />
                    <rect x="9" y="9" width="5" height="5" rx="1" />
                  </svg>
                  <span>{pipeline.node_count ?? pipeline.nodes.length} nodes</span>
                </div>
                <div className="stat">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M2 8h4m4 0h4M6 8l2-2m0 4l-2-2m4 2l2-2m0 4l-2-2" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  <span>{pipeline.edge_count ?? pipeline.edges.length} connections</span>
                </div>
              </div>

              {pipeline.metadata.tags.length > 0 && (
                <div className="pipeline-card-tags">
                  {pipeline.metadata.tags.map(tag => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              )}

              <div className="pipeline-card-footer">
                <span className="pipeline-id" title="Click to copy">
                  {pipeline.id}
                  <button
                    className="copy-id-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(pipeline.id);
                      alert('Workflow ID copied to clipboard!');
                    }}
                    title="Copy to clipboard"
                  >
                    <CopyIcon />
                  </button>
                </span>
                <span className="text-xs text-secondary">
                  Saved {formatTimestamp(pipeline.metadata.updated_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {executingPipeline && (
        <ExecutionWizard
          pipelineId={executingPipeline.id}
          pipelineName={executingPipeline.metadata.name}
          onClose={() => setExecutingPipeline(null)}
          onOpenExecution={(executionId) => {
            setExecutingPipeline(null);
            onOpenExecution(executionId);
          }}
        />
      )}
    </div>
  );
};
